const API_URL = import.meta.env.VITE_API_URL;

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface ApiResponse<T> {
  data: T | null;
  error: string | null;
  status: number;
  etag?: string | null;
}

type RefreshCallback = () => Promise<string | null>;

class ApiClient {
  private accessToken: string | null = null;
  private refreshCallback: RefreshCallback | null = null;
  private refreshPromise: Promise<string | null> | null = null;

  setAccessToken(token: string | null): void {
    this.accessToken = token;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  /**
   * Register a callback to refresh the access token.
   * Called automatically when a 401 response is received.
   */
  setRefreshCallback(callback: RefreshCallback | null): void {
    this.refreshCallback = callback;
  }

  /**
   * Attempt to refresh the access token, coalescing concurrent requests.
   * Returns the new token on success, null on failure.
   */
  private async refreshToken(): Promise<string | null> {
    if (!this.refreshCallback) {
      return null;
    }

    // Coalesce concurrent refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.refreshCallback();

    try {
      const newToken = await this.refreshPromise;
      if (newToken) {
        this.accessToken = newToken;
      }
      return newToken;
    } finally {
      this.refreshPromise = null;
    }
  }

  async request<T>(
    method: HttpMethod,
    path: string,
    options: {
      body?: unknown;
      headers?: Record<string, string>;
      skipRefresh?: boolean;
    } = {}
  ): Promise<ApiResponse<T>> {
    const makeRequest = async (token: string | null): Promise<Response> => {
      const headers: Record<string, string> = {
        ...options.headers,
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      if (options.body) {
        headers['Content-Type'] = 'application/json';
      }

      return fetch(`${API_URL}${path}`, {
        method,
        headers,
        credentials: 'include',
        body: options.body ? JSON.stringify(options.body) : undefined,
      });
    };

    try {
      let response = await makeRequest(this.accessToken);

      // Handle 401: attempt token refresh and retry (unless this is an auth endpoint)
      const isAuthEndpoint = path.startsWith('/api/auth/');
      if (response.status === 401 && !isAuthEndpoint && !options.skipRefresh) {
        const newToken = await this.refreshToken();
        if (newToken) {
          // Retry the request with the new token
          response = await makeRequest(newToken);
        }
      }

      // Handle rate limiting
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After') || '60';
        return {
          data: null,
          error: `Too many requests. Please wait ${retryAfter} seconds.`,
          status: 429,
        };
      }

      // Handle no content
      if (response.status === 204 || response.status === 304) {
        return {
          data: null,
          error: null,
          status: response.status,
          etag: response.headers.get('ETag'),
        };
      }

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        const errorMessage = data?.message || data?.error || this.getDefaultError(response.status);
        return { data: null, error: errorMessage, status: response.status };
      }

      return {
        data: data as T,
        error: null,
        status: response.status,
        etag: response.headers.get('ETag'),
      };
    } catch {
      return {
        data: null,
        error: 'Network error. Please check your connection.',
        status: 0,
      };
    }
  }

  private getDefaultError(status: number): string {
    switch (status) {
      case 400: return 'Invalid request';
      case 401: return 'Please log in to continue';
      case 403: return 'You don\'t have permission to do this';
      case 404: return 'Not found';
      case 409: return 'Conflict - resource already exists';
      case 423: return 'Account locked';
      case 500: return 'Server error. Please try again later.';
      default: return 'An error occurred';
    }
  }

  // Convenience methods
  get<T>(path: string, headers?: Record<string, string>) {
    return this.request<T>('GET', path, { headers });
  }

  post<T>(path: string, body?: unknown, headers?: Record<string, string>) {
    return this.request<T>('POST', path, { body, headers });
  }

  put<T>(path: string, body?: unknown, headers?: Record<string, string>) {
    return this.request<T>('PUT', path, { body, headers });
  }

  patch<T>(path: string, body?: unknown, headers?: Record<string, string>) {
    return this.request<T>('PATCH', path, { body, headers });
  }

  delete<T>(path: string, headers?: Record<string, string>) {
    return this.request<T>('DELETE', path, { headers });
  }
}

export const api = new ApiClient();
